import { UserProfile, UserRole } from "../../../../types/userProfile";
import { isSamePerson } from "../checks/authors";
import { isApproved, isInitialAI } from "../checks/history";
import { DetailsSheetVariant } from "../types";

type EventType = TranslationHistoryItem["eventType"];

// Pick per role (keeps order explicit)
const pickByRole = <T>(role: UserRole, admin: T, contributor: T, viewOnly: T): T =>
  role === UserRole.Admin ? admin
  : role === UserRole.Contributor ? contributor
  : viewOnly;

/** Decides the Drawer variant based on role + record history (AI/manual/no-pending). */
export const resolveDetailsSheetVariant = (
  currentUser: Pick<UserProfile, 'role' | 'userDisplayName'>,
  translationRecord: { history: Array<{ eventType: EventType; title?: string }>, translationReview?: TranslationReview | null }
): DetailsSheetVariant => {
  const { history, translationReview } = translationRecord;
  const { role, userDisplayName } = currentUser;

  // 1) Approved at least once → NoPending per role
  if (isApproved(history)) {
    return pickByRole(
      role,
      DetailsSheetVariant.Admin_NoPending,
      DetailsSheetVariant.Contributor_NoPending,
      DetailsSheetVariant.ViewOnly_NoPending
    );
  }

  // 2) AI initial submission pending → AI per role
  if (isInitialAI(history)) {
    return pickByRole(
      role,
      DetailsSheetVariant.Admin_AI,
      DetailsSheetVariant.Contributor_AI,
      DetailsSheetVariant.ViewOnly_AI
    );
  }

  // 3) Manual submission (Submitted by {Name})
  const submittedBy = translationReview?.author;
  if (submittedBy) {
    if (role === UserRole.Admin) return DetailsSheetVariant.Admin_Manual;
    if (role === UserRole.Contributor) {
      return isSamePerson(submittedBy, userDisplayName)
        ? DetailsSheetVariant.Contributor_ManualOwn
        : DetailsSheetVariant.Contributor_ManualOther;
    }
    return DetailsSheetVariant.ViewOnly_ManualOther;
  }

  // 4) Fallback → NoPending per role
  return pickByRole(
    role,
    DetailsSheetVariant.Admin_NoPending,
    DetailsSheetVariant.Contributor_NoPending,
    DetailsSheetVariant.ViewOnly_NoPending
  );
};

